import { useNavigate, useParams, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";

import toast from "react-hot-toast";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  axiosWithToast,
  dateToString,
  HtmlTextFormatter,
  ItemStatus,
} from "../../../components/Utils";
import { PMField } from "../../../components/utils/PMField";

import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  useUserHasAccess,
  useWorkOrderPermissions,
  useProjectWorkOrdersAccess,
  useCompanyHasModule,
} from "../../../routes/roleOnly";
import { useSelector, useDispatch } from "react-redux";

import { PMNotice } from "../../../components/utils/notifications";
import {
  CheckBadgeIcon,
  TrashIcon,
  ClockIcon,
  CubeIcon,
  PencilIcon,
  ChevronRightIcon,
  PlusIcon,
  PrinterIcon,
  ClipboardDocumentIcon,
  ArrowLeftIcon,
  CheckIcon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  FlagIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  Squares2X2Icon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import "./work-order-redesign.css";
import { PMDialog } from "../../../components/utils/PMDialog";
import { PMButton } from "../../../components/utils/PMButton";
import PMSwitch from "../../../components/utils/PMSwitch";
import ProConfirmation from "../../../components/utils/ProConfirmation";
import {
  createWorkOrder,
  deleteWorkOrder,
  fetchWorkOrder,
  updateWorkOrder,
} from "../../../api/work-order";
import { fetchProject } from "../../../api/project";
import {
  WorkOrderEditForm,
  formatSuggestedWorkOrderTitleFromDeviation,
  suggestedWorkOrderTitleFromProject,
} from "../../../components/dashboard/workOrders/work-order-edit-form";
import { TinyDocumentSection } from "../../../components/dashboard/documents/tinyDocumentSection";
import WorkOrderItemConsumptionList from "../../../components/dashboard/workOrders/work-order-item-consumption-list";

import { getHoursAsync } from "../../../slices/hour";

import { NotesAdvancedSection } from "../../../components/dashboard/notes/notesAdvancedSection";
import { StatusUpdateSection } from "../../../components/dashboard/statusUpdates/StatusUpdateSection";
import { WorkOrderPrintDialog } from "../../../components/dashboard/workOrders/work-order-print-dialog";
import { WorkOrderPlanSection } from "../../../components/dashboard/workOrders/work-order-plan-section";
import WorkOrderFinishDialog from "../../../components/dashboard/workOrders/work-order-finish-dialog";
import { WorkOrderBillingReadyDialog } from "../../../components/dashboard/workOrders/work-order-billing-ready-dialog";
import { WorkOrderAdminMenu } from "../../../components/dashboard/workOrders/work-order-admin-menu";
import { WorkOrderAdminSetStatusDialog } from "../../../components/dashboard/workOrders/work-order-admin-set-status-dialog";
import { TimeRegistrationSection } from "../../../components/dashboard/hours/time-registration-section";
import { getChecklistStatus } from "../../../components/dashboard/service/reports/utils";

/** Circular progress ring used in the work-order hero. */
function ProgressRing({ pct, size = 58 }) {
  const value = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="progress-ring"
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth="5" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--primary)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - value / 100)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset .5s cubic-bezier(.2,.8,.2,1)" }}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        className="mono"
        style={{ fontSize: 13, fontWeight: 700, fill: "var(--ink)" }}
      >
        {value}%
      </text>
    </svg>
  );
}

/** Maps a work order to one of the four visual status buckets. */
function workOrderStatusBucket(wo) {
  if (!wo) return "draft";
  if (
    wo.is_completed ||
    [
      "finished",
      "invoiced",
      "processed",
      "ready_invoice",
      "processing",
      "approved",
    ].includes(wo.status)
  ) {
    return wo.status === "processing" ? "progress" : "done";
  }
  if (wo.status === "ongoing") return "progress";
  if (wo.status === "rejected" || wo.status === "draft" || !wo.status) return "draft";
  // updated / cancelled / unknown → scheduled (planned) until work progresses again
  return "scheduled";
}

const STATUS_BUCKET_CLASS = {
  draft: "st-draft",
  scheduled: "st-scheduled",
  progress: "st-progress",
  done: "st-done",
};

/** True when the deadline has passed and the order is not yet completed. */
function isWorkOrderOverdue(wo) {
  if (!wo?.due_date || wo.is_completed) return false;
  if (workOrderStatusBucket(wo) === "done") return false;
  const due = new Date(wo.due_date);
  if (Number.isNaN(due.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

/** Field-log action card (Hours / Materials / Notes) that opens a dialog. */
function LogCard({ icon: Icon, tint, color, title, meta, value, unit, onOpen, disabled }) {
  return (
    <button
      type="button"
      className="log-card"
      onClick={onOpen}
      disabled={disabled}
    >
      <div className="lc-top">
        <span className="lc-ico" style={{ background: tint, color }}>
          <Icon className="h-5 w-5" />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="lc-title">{title}</div>
          <div className="lc-meta">{meta}</div>
        </div>
        <PlusIcon className="h-5 w-5" style={{ color }} />
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span className="lc-val" style={{ color }}>
          {value}
        </span>
        <span className="lc-unit">{unit}</span>
      </div>
    </button>
  );
}

export const ReadWorkOrder = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const isMananger = useUserHasAccess({ role: "projects_work-orders", action: "manage" });
  const canAccessProjectPlan = useUserHasAccess({ roles: ["projects_plan"] });
  // const isMananger = false;
  const currentUser = useSelector((state) => state.session.currentUser);
  const dateToday = new Date().toISOString().substring(0, 10);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [workOrderPrintDialogOpen, setWorkOrderPrintDialogOpen] =
    useState(false);
  const [workOrderFinishDialogOpen, setWorkOrderFinishDialogOpen] =
    useState(false);
  const [billingReadyDialogOpen, setBillingReadyDialogOpen] = useState(false);
  const [adminSetStatusDialogOpen, setAdminSetStatusDialogOpen] =
    useState(false);

  const [customer, setCustomer] = useState(
    location?.state?.customer || props?.customer
  );
  const [project, setProject] = useState(
    location?.state?.project || props?.project
  );
  const [delivery_address, setDeliveryAddress] = useState(
    location?.state?.delivery_address || props?.delivery_address
  );

  const navigate = useNavigate(); // Assuming you have a navigate function from react-router-dom

  // Edit/create are now served by this same page (the separate EditWorkOrder
  // page has been merged in). The mode is derived from the URL so existing
  // navigation (.../new and .../edit/:id) keeps working unchanged.
  const isNewRoute = location.pathname.endsWith("/work_orders/new");
  const isEditRoute = /\/work_orders\/edit\/[^/]+/.test(location.pathname);
  // Creating a brand-new order still uses the full-page form. Editing an
  // existing order now happens in a right-side drawer over the read view, so
  // `/edit/:id` simply opens the drawer (the standalone edit page is retired).
  const editing = isNewRoute;
  const [editDrawerOpen, setEditDrawerOpen] = useState(isEditRoute);

  const returnAfterWorkOrderCreate =
    location.state?.return_after_work_order_create ?? null;

  const [work_order, setWorkOrder] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [newProjectActivityTitle, setNewProjectActivityTitle] = useState("");
  const [creatingProjectActivity, setCreatingProjectActivity] = useState(false);
  const newWoProjectHydratedRef = useRef(null);

  // Opening `/edit/:id` (e.g. from the admin menu or project plan) opens the
  // edit drawer over the read view rather than the retired edit page.
  useEffect(() => {
    if (isEditRoute) setEditDrawerOpen(true);
  }, [isEditRoute]);

  const closeEditDrawer = useCallback(() => {
    setEditDrawerOpen(false);
    if (isEditRoute && id) {
      navigate(`/dashboard/work_orders/${id}`, { replace: true });
    }
  }, [isEditRoute, id, navigate]);

  // Escape to close + lock background scroll while the edit drawer is open.
  useEffect(() => {
    if (!editDrawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeEditDrawer();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [editDrawerOpen, closeEditDrawer]);

  // Use the new permissions hook
  const permissions = useWorkOrderPermissions(work_order);
  const workOrdersModuleAccess = useProjectWorkOrdersAccess(
    work_order?.project ?? project
  );
  const qualityChecklistsModule = useCompanyHasModule("quality_checklists");
  const canEditWorkOrderUi =
    Boolean(permissions?.canEdit) && workOrdersModuleAccess.canEdit;
  const canDeleteWorkOrderInModule = workOrdersModuleAccess.canDelete;
  const canCreateWorkOrderInModule = workOrdersModuleAccess.canCreate;
  // Fields are mutable when creating a new order (with create rights) or when
  // editing an existing one the user may edit.
  const canMutateWorkOrderFields =
    (!work_order?.id && canCreateWorkOrderInModule) || canEditWorkOrderUi;

  const workOrderActivityListParams = useMemo(() => {
    const p = { selectable_for_work_order: true };
    if (work_order?.id) p.work_order_id = work_order.id;
    return p;
  }, [work_order?.id]);

  const [initialValues, setInitialValues] = useState({
    id: null,
    title: "",
    number: "",
    order_date: dateToday,
    due_date: dateToday,
    project_id: project?.id,
    description: "",
    is_urgent: false,
    budgeted_hours: 0,
    cost_included_in_project: false,
    sales_income_included_in_project: false,
    do_not_expect_income: false,
    uses_checklist: false,
    participants: [],
    project_activity_id: null,
    project_activity: null,
    submit: null,
  });

  // Hours fetching logic (similar to report-finish.js)
  const dispatch = useDispatch();
  const hoursState = useSelector((store) => store.hours);

  const [timeRegistrationDialogSignal, setTimeRegistrationDialogSignal] =
    useState(0);

  const [hoursController, setHoursController] = useState({
    query: "",
    page: 1,
    per_page: 100, // Get more hours to ensure we have all
    sortBy: "created_at",
    sort: "desc",
    filters: [],
    view: null,
    fields: "time,effective_hourly_rate",
  });

  // Calculate total registered hours from hours data
  const totalRegisteredHours =
    hoursState?.data?.reduce((sum, hour) => {
      return sum + (parseFloat(hour.time) || 0);
    }, 0) || 0;

  // Calculate total material costs from work order items
  const totalMaterialCosts =
    work_order?.items?.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const purchasePrice = parseFloat(item.purchase_price) || 0;
      return sum + quantity * purchasePrice;
    }, 0) || 0;

  // Use work_order.budgeted_hours for the budget
  const budgetedHours = work_order?.budgeted_hours || 0;

  // Calculate progress percentage
  const progressPercentage =
    budgetedHours > 0
      ? Math.min((totalRegisteredHours / budgetedHours) * 100, 100)
      : 0;

  // Fetch hours when work order is loaded
  useEffect(() => {
    if (work_order?.id) {
      const filters = [{ name: "WORK_ORDER", value: work_order.id }];
      setHoursController((prev) => ({
        ...prev,
        filters: filters,
      }));
      dispatch(getHoursAsync({ ...hoursController, filters }));
    }
  }, [work_order?.id]);

  useEffect(() => {
    // if ID is set, fetch the work_order
    setTimeout(() => {
      if (id) {
        setIsLoading(true);
        getWorkOrder(id);
      } else {
        setIsLoading(false);
      }
    }, 500);
  }, [id]);

  // Update state when location.state changes
  useEffect(() => {
    if (location?.state) {
      setCustomer(location.state.customer || customer);
      setProject(location.state.project || project);
      setDeliveryAddress(location.state.delivery_address || delivery_address);
    }
  }, [location?.state]);

  useEffect(() => {
    if (work_order) {
      setInitialValues({
        id: work_order.id,
        title: work_order?.title,
        number: work_order?.number,
        order_date: work_order?.order_date || dateToday,
        due_date: work_order?.due_date || dateToday,
        project_id: work_order?.project_id || project?.id,
        customer_id: work_order?.customer_id || customer?.id,
        delivery_address_id:
          work_order?.delivery_address_id || delivery_address?.id,
        description: work_order?.description || "",
        budgeted_hours: work_order?.budgeted_hours || 0,
        cost_included_in_project:
          work_order?.cost_included_in_project || false,
        sales_income_included_in_project:
          work_order?.sales_income_included_in_project || false,
        do_not_expect_income: work_order?.do_not_expect_income || false,
        uses_checklist: work_order?.uses_checklist || false,
        participants: work_order?.participants || [],
        project_activity_id:
          work_order?.project_activity_id ??
          work_order?.project_activity?.id ??
          null,
        project_activity: work_order?.project_activity ?? null,
        submit: null,
      });
    } else {
      const paFromNav = location?.state?.project_activity;
      const paIdFromNav = location?.state?.project_activity_id;

      setInitialValues((prev) => {
        const deviationHint = location?.state?.work_order_title_from_deviation;
        const fromDeviation = formatSuggestedWorkOrderTitleFromDeviation(
          deviationHint,
          t
        );
        const suggestedTitle =
          fromDeviation || suggestedWorkOrderTitleFromProject(project) || "";
        const nextCustomerId =
          customer?.id ?? project?.customer?.id ?? prev.customer_id ?? null;
        const nextDeliveryId =
          delivery_address?.id ??
          project?.delivery_address?.id ??
          prev.delivery_address_id ??
          null;

        const titleKept =
          typeof prev.title === "string" && prev.title.trim() !== ""
            ? prev.title
            : suggestedTitle || "";

        return {
          id: null,
          title: titleKept,
          number: "",
          order_date: dateToday,
          due_date: dateToday,
          project_id: project?.id ?? prev.project_id,
          customer_id: nextCustomerId,
          delivery_address_id: nextDeliveryId,
          description: "",
          is_urgent: false,
          budgeted_hours: 0,
          cost_included_in_project: false,
          sales_income_included_in_project: false,
          do_not_expect_income: false,
          uses_checklist: false,
          participants: [],
          project_activity_id:
            paFromNav?.id ??
            (paIdFromNav != null && paIdFromNav !== ""
              ? Number(paIdFromNav) || paIdFromNav
              : null),
          project_activity: paFromNav ?? null,
          submit: null,
        };
      });
    }
  }, [
    work_order,
    isMananger,
    currentUser.id,
    dateToday,
    project?.id,
    project?.displayTitle,
    project?.project_number,
    project?.name,
    project?.customer?.id,
    project?.delivery_address?.id,
    customer?.id,
    delivery_address?.id,
    location?.state?.project_activity,
    location?.state?.project_activity_id,
    location?.state?.work_order_title_from_deviation,
    t,
  ]);

  // Hydrate project/customer/delivery for a brand-new order opened with a
  // project id but without the nested records in navigation state.
  useEffect(() => {
    if (id) return;
    const pid = project?.id;
    if (!pid) return;
    if (newWoProjectHydratedRef.current === pid) return;
    newWoProjectHydratedRef.current = pid;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchProject(pid, {
          fields: "displayTitle,project_number,name,customer,delivery_address",
        });
        const p = res?.data?.data ?? res?.data;
        if (cancelled || !p?.id) return;
        setProject((prev) => ({ ...(prev || {}), ...p }));
        if (p.customer) setCustomer(p.customer);
        if (p.delivery_address) setDeliveryAddress(p.delivery_address);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, project?.id]);

  const getWorkOrder = async (id) => {
    setIsLoading(true);
    setAccessDenied(false);

    try {
      const res = await fetchWorkOrder(id, { fields: "*" });
      const wo = res?.data?.data ?? res?.data;
      setWorkOrder(wo);
      if (wo?.project) setProject(wo.project);
      if (wo?.customer) setCustomer(wo.customer);
      if (wo?.delivery_address) setDeliveryAddress(wo.delivery_address);
    } catch (error) {
      console.error("Error fetching work order:", error);

      if (error.response && error.response.status === 403) {
        setAccessDenied(true);
        toast.error("Du har ikke tilgang til denne arbeidsordren");
      } else {
        toast.error("Something went wrong when trying to load the work order");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createOrUpdate = async (data) => {
    try {
      if (work_order?.id) {
        const result = await updateWorkOrder(data, { params: { fields: "*" } });
        return result;
      } else {
        const result = await createWorkOrder(data, { fields: "*" });
        return result;
      }
    } catch (error) {
      console.error("Error in createOrUpdate:", error);
      throw error;
    }
  };

  // get userType form searchParams
  const searchParams = new URLSearchParams(location.search);
  const _userType = searchParams.get("userType") || "";

  const [errors, setErrors] = useState([]);

  // remove errors after 6 seconds
  useEffect(() => {
    if (errors && errors.length > 0) {
      setTimeout(() => {
        setErrors([]);
      }, 20000);
    }
  }, [errors]);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const [itemConsumptionDialogOpen, setItemConsumptionDialogOpen] =
    useState(false);
  const [costEstimationOpen, setCostEstimationOpen] = useState(false);
  const statusUpdatesRef = useRef(null);

  const handleParticipantsChange = useCallback((next) => {
    setWorkOrder((w) => (w ? { ...w, participants: next } : w));
    setInitialValues((prev) => ({ ...prev, participants: next }));
  }, []);

  const scrollToStatusUpdates = useCallback(() => {
    statusUpdatesRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const canMarkReadyForInvoicing = useMemo(() => {
    if (!work_order?.id) return false;
    if (work_order?.order?.id) return false;
    if (!work_order.is_completed) return false;
    if (work_order.sales_income_included_in_project) return false;
    if (["cancelled", "invoiced", "draft", "ongoing"].includes(work_order.status))
      return false;
    const role = work_order.role_for_current_user;
    const roleNames = (currentUser?.roles || []).map((r) => r.name);
    if (roleNames.includes("admin")) return true;
    if (!roleNames.includes("projects_work-orders_edit")) return false;
    return ["RESPONSIBLE", "RESPONSIBLE_EXECUTOR"].includes(role);
  }, [work_order, currentUser?.roles]);

  const canOpenLinkedSalesOrder = useMemo(() => {
    if (!work_order?.order?.id) return false;
    const roleNames = (currentUser?.roles || []).map((r) => r.name);
    if (roleNames.includes("admin")) return true;
    if (!roleNames.includes("projects_work-orders_edit")) return false;
    const role = work_order.role_for_current_user;
    return ["RESPONSIBLE", "RESPONSIBLE_EXECUTOR"].includes(role);
  }, [work_order, currentUser?.roles]);

  const canAdminSetStatus = useMemo(() => {
    if (!work_order?.id) return false;
    if (!workOrdersModuleAccess.canRead) return false;
    return (currentUser?.roles || []).some((r) => r.name === "admin");
  }, [work_order?.id, workOrdersModuleAccess.canRead, currentUser?.roles]);

  const hasWorkOrderAdminAccess = useMemo(
    () => isMananger || workOrdersModuleAccess.canEdit,
    [isMananger, workOrdersModuleAccess.canEdit]
  );

  const showWorkOrderAdminMenu = useMemo(() => {
    if (!work_order?.id) return false;
    return (
      hasWorkOrderAdminAccess ||
      canMarkReadyForInvoicing ||
      canOpenLinkedSalesOrder ||
      canAdminSetStatus ||
      (isMananger && !work_order?.is_approved)
    );
  }, [
    work_order?.id,
    work_order?.is_approved,
    hasWorkOrderAdminAccess,
    canMarkReadyForInvoicing,
    canOpenLinkedSalesOrder,
    canAdminSetStatus,
    isMananger,
  ]);

  const canShowWorkOrderApproveInMenu = useMemo(
    () => Boolean(isMananger && work_order?.id && !work_order?.is_approved),
    [isMananger, work_order?.id, work_order?.is_approved]
  );

  /** Fullført AO: alle med lesetilgang kan åpne dialog. Ellers: samme vilkår som «Ferdigstill» i header. */
  const canUseFinishDialog = useMemo(() => {
    if (!work_order?.id) return false;
    if (work_order.is_completed) return true;
    // Allow finish after approve/update as well — approval alone does not set is_completed.
    if (
      !["draft", "ongoing", "rejected", "approved", "updated"].includes(
        work_order.status
      )
    )
      return false;
    return Boolean(
      canEditWorkOrderUi || permissions?.canFinishWorkOrder
    );
  }, [
    work_order?.id,
    work_order?.is_completed,
    work_order?.status,
    canEditWorkOrderUi,
    permissions?.canFinishWorkOrder,
  ]);

  const headerActions = useMemo(() => {
    if (editing) {
      return [
        {
          component: null,
          label: t("work_orders.back_to_view"),
          description: "",
          path: null,
          action: () =>
            navigate(
              work_order?.id
                ? `/dashboard/work_orders/${work_order.id}`
                : "/dashboard/work_orders"
            ),
          icon: ArrowLeftIcon,
          textColor: "text-teal-700",
          bgColor: "bg-white",
          hidden: false,
          menu: false,
        },
        {
          component: null,
          label: "Skriv ut",
          description: "",
          path: null,
          action: () => setWorkOrderPrintDialogOpen(true),
          icon: PrinterIcon,
          textColor: "text-teal-700",
          bgColor: "bg-white",
          hidden: !work_order?.id,
          menu: false,
        },
        {
          component: null,
          label: "Slett",
          description: "",
          path: null,
          action: () => setDeleteConfirmationOpen(true),
          icon: TrashIcon,
          textColor: "text-teal-700",
          bgColor: "bg-white",
          hidden: !(canDeleteWorkOrderInModule || isMananger),
          menu: true,
        },
      ];
    }

    const canOpenEditForFieldStaff =
      Boolean(workOrdersModuleAccess.canRead) &&
      (Boolean(permissions?.canExecute) ||
        Boolean(permissions?.canAddNote) ||
        Boolean(permissions?.canWorkNotes));

    return [
      {
        component: null,
        label: "Rediger",
        description: "",
        path: `/dashboard/work_orders/edit/${work_order?.id}`,
        action: null,
        icon: PencilIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden:
          (!canEditWorkOrderUi && !canOpenEditForFieldStaff) || !work_order?.id,
        menu: false,
      },
      {
        component: null,
        label: "Skriv ut",
        description: "",
        path: null,
        action: () => setWorkOrderPrintDialogOpen(true),
        icon: PrinterIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden: !work_order?.id,
        menu: false,
      },
      {
        component: null,
        label: "Ferdigstill",
        description: "",
        path: null,
        action: () => setWorkOrderFinishDialogOpen(true),
        icon: CheckBadgeIcon,
        textColor: "text-emerald-700",
        bgColor: "bg-emerald-100",
        hidden:
          (!canEditWorkOrderUi && !permissions?.canFinishWorkOrder) ||
          work_order?.is_completed ||
          !["draft", "ongoing", "rejected"].includes(work_order?.status),
        menu: false,
      },
      {
        component: null,
        label: "Slett",
        description: "",
        path: null,
        action: () => setDeleteConfirmationOpen(true),
        icon: TrashIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden: !(canDeleteWorkOrderInModule || isMananger),
        menu: true,
      },
    ];
  }, [
    work_order?.id,
    work_order?.status,
    work_order?.is_completed,
    isMananger,
    canEditWorkOrderUi,
    permissions?.canFinishWorkOrder,
    workOrdersModuleAccess.canRead,
    permissions?.canExecute,
    permissions?.canAddNote,
    permissions?.canWorkNotes,
    canDeleteWorkOrderInModule,
    setDeleteConfirmationOpen,
    setWorkOrderPrintDialogOpen,
    setWorkOrderFinishDialogOpen,
    editing,
    navigate,
    t,
  ]);

  const headerMeta = useMemo(
    () => [
      {
        label: `Opprettet ${dateToString(work_order?.order_date)}`,
        hidden: !work_order?.order_date,
      },
      {
        label: `Frist ${dateToString(work_order?.due_date)}`,
        hidden: !work_order?.due_date,
      },
      {
        component: <ItemStatus item={work_order} />,
        hidden: false,
      },
      {
        label: `Godkjent av ${work_order?.approved_by?.displayTitle}`,
        hidden: !work_order?.is_approved,
      },
    ],
    [
      work_order,
      work_order?.is_approved,
      work_order?.approved_by?.displayTitle,
      work_order?.order_date,
      work_order?.due_date,
    ]
  );

  const headerSummaryItems = useMemo(
    () => [
      {
        label: "Prosjekt",
        value: work_order?.project?.displayTitle || "-",
        path: work_order?.project?.id
          ? `/dashboard/projects/${work_order.project.id}`
          : null,
      },
      {
        label: t("work_orders.project_activity.linked_label"),
        value:
          work_order?.project_activity?.display_title ||
          work_order?.project_activity?.title ||
          "-",
      },
      {
        label: "Kunde",
        value: work_order?.customer?.displayTitle || "-",
        path: work_order?.customer?.id
          ? `/dashboard/customers/${work_order.customer.id}`
          : null,
      },
      {
        label: "Budsjetterte timer",
        value: `${work_order?.budgeted_hours || 0} timer`,
      },
    ],
    [
      t,
      work_order?.project?.displayTitle,
      work_order?.project?.id,
      work_order?.project_activity?.display_title,
      work_order?.project_activity?.title,
      work_order?.customer?.displayTitle,
      work_order?.customer?.id,
      work_order?.budgeted_hours,
    ]
  );

  const headerSummaryFooter = useMemo(() => {
    if (!work_order?.description) return null;

    return (
      <>
        <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Beskrivelse
        </h4>
        <div className="prose prose-sm mt-3 max-w-none text-gray-700">
          <HtmlTextFormatter string={work_order.description} />
        </div>
      </>
    );
  }, [work_order?.description]);

  const handleDeleteWorkOrder = () => {
    if (!work_order?.id) return;
    setDeleteConfirmationOpen(false);
    setIsLoading(true);
    deleteWorkOrder(work_order.id)
      .then(() => {
        setIsLoading(false);
        toast.success(t("work_orders.delete_success"));
        navigate("/dashboard/work_orders");
      })
      .catch((err) => {
        setIsLoading(false);
        const raw = err?.response?.data?.errors;
        const msg = Array.isArray(raw)
          ? raw[0]
          : typeof raw === "string"
            ? raw
            : null;
        toast.error(msg || t("work_orders.delete_failed"));
      });
  };

  const Content = useCallback(() => {
    const bucket = workOrderStatusBucket(work_order);
    const statusLabel = t(`work_order.redesign.status.${bucket}`);
    const descriptionText = (work_order?.description || "")
      .replace(/<[^>]*>/g, "")
      .trim();
    const crew = (work_order?.participants || []).filter((p) => p?.user);
    const completionReqs = [
      {
        k: "crew",
        label: t("work_order.redesign.req_crew"),
        met: crew.length > 0,
        s: String(crew.length),
      },
      {
        k: "hours",
        label: t("work_order.redesign.req_hours"),
        met: totalRegisteredHours > 0,
        s: `${totalRegisteredHours.toFixed(1)}t`,
      },
      {
        k: "desc",
        label: t("work_order.redesign.req_desc"),
        met: descriptionText.length >= 5,
        s: descriptionText.length >= 5 ? "✓" : "—",
      },
      {
        k: "items",
        label: t("work_order.redesign.req_items"),
        met: (work_order?.items?.length || 0) > 0,
        s: String(work_order?.items?.length || 0),
      },
    ];
    const metCount = completionReqs.filter((r) => r.met).length;
    const completionPct = Math.round((metCount / completionReqs.length) * 100);
    const isDone = Boolean(work_order?.is_completed) || bucket === "done";

    const detailRows = [
      {
        icon: Squares2X2Icon,
        label: t("work_order.redesign.detail_project"),
        value: work_order?.project?.displayTitle || "—",
        to: work_order?.project?.id
          ? `/dashboard/projects/${work_order.project.id}`
          : null,
        id: work_order?.project?.id ? `#${work_order.project.id}` : null,
      },
      {
        icon: BuildingOffice2Icon,
        label: t("work_order.redesign.detail_customer"),
        value: work_order?.customer?.displayTitle || "—",
        to: work_order?.customer?.id
          ? `/dashboard/customers/${work_order.customer.id}`
          : null,
        id: work_order?.customer?.id ? `#${work_order.customer.id}` : null,
      },
      {
        icon: FlagIcon,
        label: t("work_orders.project_activity.linked_label"),
        value:
          work_order?.project_activity?.display_title ||
          work_order?.project_activity?.title ||
          "—",
      },
      {
        icon: CalendarDaysIcon,
        label: t("work_order.redesign.due"),
        value: work_order?.due_date ? dateToString(work_order.due_date) : "—",
        danger: isWorkOrderOverdue(work_order),
      },
    ];

    return (
      <div className="wo-redesign mx-auto mt-6 w-full max-w-[1320px] px-3 sm:px-6 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-8">
        <div className="wo-layout">
          {/* ============ MAIN COLUMN ============ */}
          <div className="wo-col">
            {work_order?.is_rejected && (
              <PMNotice
                severity="error"
                title="Arbeidsordre ikke godkjent"
                description={
                  <div className="space-y-2">
                    {work_order?.approval_note && (
                      <div>
                        <span className="font-medium">Begrunnelse:</span>{" "}
                        {work_order.approval_note}
                      </div>
                    )}
                    <div className="text-sm">
                      <span className="font-medium">Behandlet av:</span>{" "}
                      {work_order?.approval_by?.displayTitle || "Ukjent"}
                    </div>
                  </div>
                }
              />
            )}

            {work_order?.sales_income_included_in_project ? (
              <PMNotice
                severity="info"
                title={t(
                  "work_orders.billing_ready.sales_income_in_project_notice_title"
                )}
                description={t(
                  "work_orders.billing_ready.sales_income_in_project_notice"
                )}
              />
            ) : null}

            {/* ---- What to do / description (first) ---- */}
            <div className="card">
              <div className="card-head">
                <span
                  className="sec-ico"
                  style={{ background: "var(--primary-tint)", color: "var(--primary)" }}
                >
                  <DocumentTextIcon className="h-[17px] w-[17px]" />
                </span>
                <h2>{t("work_order.redesign.what_to_do")}</h2>
              </div>
              <div className="card-pad desc">
                {descriptionText.length > 0 ? (
                  <HtmlTextFormatter string={work_order?.description} />
                ) : (
                  <div className="empty">{t("work_order.redesign.no_description")}</div>
                )}
                {work_order?.id && (
                  <div className="mt-4">
                    <TinyDocumentSection
                      module="work_order_description"
                      moduleParam={work_order?.id}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* ---- Scheduler ---- */}
            {work_order?.id &&
              canAccessProjectPlan &&
              (work_order?.project_id || project?.id) && (
                <WorkOrderPlanSection
                  workOrder={work_order}
                  project={project}
                  canEdit={canEditWorkOrderUi}
                />
              )}

            {/* ---- Field log (PC only; on mobile these live in the bottom nav) ---- */}
            {work_order?.id && (
              <div className="card wo-fieldlog">
                <div className="card-head">
                  <span
                    className="sec-ico"
                    style={{ background: "var(--green-tint)", color: "var(--green)" }}
                  >
                    <WrenchScrewdriverIcon className="h-[17px] w-[17px]" />
                  </span>
                  <h2>{t("work_order.redesign.field_log")}</h2>
                  <span className="sub">{t("work_order.redesign.field_log_sub")}</span>
                </div>
                <div className="card-pad">
                  <div className="log-grid">
                    <LogCard
                      icon={ClockIcon}
                      tint="var(--blue-tint)"
                      color="var(--blue)"
                      title={t("work_order.redesign.hours")}
                      meta={`${totalRegisteredHours.toFixed(1)} t`}
                      value={totalRegisteredHours.toFixed(1)}
                      unit={
                        budgetedHours > 0
                          ? `/ ${budgetedHours} ${t("work_order.redesign.budget_hours_unit")}`
                          : t("work_order.redesign.budget_hours_unit")
                      }
                      onOpen={() => setTimeRegistrationDialogSignal((n) => n + 1)}
                    />
                    <LogCard
                      icon={CubeIcon}
                      tint="var(--violet-tint)"
                      color="var(--violet)"
                      title={t("work_order.redesign.materials")}
                      meta={`${work_order?.items?.length || 0} ${t(
                        "work_order.redesign.material_lines"
                      )}`}
                      value={Math.round(totalMaterialCosts).toLocaleString()}
                      unit="kr"
                      disabled={!permissions.canItemConsumption}
                      onOpen={() =>
                        permissions.canItemConsumption
                          ? setItemConsumptionDialogOpen(true)
                          : navigate(
                              `/dashboard/work_orders/${work_order.id}/item-consumption`
                            )
                      }
                    />
                    <LogCard
                      icon={ChatBubbleLeftRightIcon}
                      tint="var(--amber-tint)"
                      color="var(--amber)"
                      title={t("work_order.redesign.status_updates_title")}
                      meta={t("work_order.redesign.status_updates_meta")}
                      value=""
                      unit={t("work_order.redesign.open")}
                      onOpen={scrollToStatusUpdates}
                    />
                    {qualityChecklistsModule &&
                    (work_order?.uses_checklist || work_order?.checklist_id) ? (
                      (() => {
                        const checklistStatus = getChecklistStatus(
                          work_order?.checklist
                        );
                        return (
                          <LogCard
                            icon={ClipboardDocumentIcon}
                            tint="var(--primary-tint)"
                            color="var(--primary)"
                            title={t("work_orders.checklist.card_title")}
                            meta={checklistStatus.statusText || ""}
                            value={String(checklistStatus.completedCount || 0)}
                            unit={`/ ${checklistStatus.totalCount || 0}`}
                            onOpen={() =>
                              navigate(
                                `/dashboard/work_orders/${work_order.id}/checklist`
                              )
                            }
                          />
                        );
                      })()
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* Oppdatering — single write path for status updates (mobile + desktop). */}
            {work_order?.id && (
              <div
                ref={statusUpdatesRef}
                id="wo-status-updates"
                className="card wo-status-updates scroll-mt-20"
              >
                <div className="card-pad space-y-4">
                  <StatusUpdateSection
                    subjectType="work_order"
                    subjectId={work_order.id}
                    customer={work_order?.customer || work_order?.project?.customer}
                    projectId={work_order?.project_id || work_order?.project?.id}
                    project={work_order?.project || null}
                    title={t("work_order.redesign.status_updates_title")}
                    canCompose={Boolean(permissions?.canAddNote)}
                  />
                  <NotesAdvancedSection
                    module="work_order"
                    module_param={work_order.id}
                    legacyOnly
                    embedded
                    title={t("notes.legacy_title")}
                  />
                </div>
              </div>
            )}

            {/* Headless time-registration section: no visible card, but its
                PMDialog (portaled) opens via the signal from the Hours card or
                the mobile bottom nav. Always mounted so it works on mobile too. */}
            {work_order?.id && (
              <div style={{ display: "none" }}>
                <TimeRegistrationSection
                  module="work_order"
                  moduleId={work_order?.id}
                  project={work_order?.project}
                  workOrder={work_order}
                  onRefresh={() => {
                    if (work_order?.id) {
                      const filters = [
                        { name: "WORK_ORDER", value: work_order.id },
                      ];
                      dispatch(getHoursAsync({ ...hoursController, filters }));
                    }
                  }}
                  openRegistrationDialogSignal={timeRegistrationDialogSignal}
                />
              </div>
            )}
          </div>

          {/* ============ RAIL ============ */}
          <div className="wo-col wo-rail">
            {/* Ready to complete */}
            <div className="card complete-card">
              <div className="complete-head">
                <h2>{t("work_order.redesign.ready_title")}</h2>
                <p>
                  {metCount === completionReqs.length
                    ? t("work_order.redesign.all_met")
                    : t("work_order.redesign.items_left", {
                        count: completionReqs.length - metCount,
                      })}
                </p>
                <div className="progbar" style={{ marginTop: 12 }}>
                  <i style={{ width: completionPct + "%" }} />
                </div>
              </div>
              {completionReqs.map((r) => (
                <div className={"req" + (r.met ? " met" : "")} key={r.k}>
                  <span
                    className="rk"
                    style={
                      r.met
                        ? { background: "var(--green)", borderColor: "var(--green)", color: "#fff" }
                        : undefined
                    }
                  >
                    <CheckIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="rt">{r.label}</span>
                  <span className="rs">{r.s}</span>
                </div>
              ))}
              <div className="card-pad" style={{ borderTop: "1px solid var(--border)" }}>
                <button
                  type="button"
                  className="wbtn primary"
                  style={{ width: "100%" }}
                  disabled={isDone || !canUseFinishDialog}
                  onClick={() => setWorkOrderFinishDialogOpen(true)}
                >
                  <CheckCircleIcon className="h-[18px] w-[18px]" />
                  {isDone
                    ? t("work_order.redesign.completed")
                    : t("work_order.redesign.mark_complete")}
                </button>
              </div>
            </div>

            {/* Details */}
            <div className="card">
              <div className="card-head">
                <span
                  className="sec-ico"
                  style={{ background: "var(--surface-3)", color: "var(--ink-2)" }}
                >
                  <DocumentTextIcon className="h-4 w-4" />
                </span>
                <h2>{t("work_order.redesign.details")}</h2>
                <span className="spacer" />
                {canEditWorkOrderUi ? (
                  <button
                    type="button"
                    className="wbtn sm ghost"
                    onClick={() => setEditDrawerOpen(true)}
                  >
                    <PencilIcon className="h-3.5 w-3.5" /> {t("edit")}
                  </button>
                ) : null}
              </div>
              <div className="alist">
                {detailRows.map((r, i) => {
                  const Icon = r.icon;
                  const body = (
                    <>
                      <span
                        className="ai-ico"
                        style={{ background: "var(--surface-2)", color: "var(--ink-3)" }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="ai-s">{r.label}</div>
                        <div
                          className="ai-t"
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            ...(r.danger ? { color: "var(--red)" } : {}),
                          }}
                        >
                          {r.value}
                        </div>
                      </div>
                      {r.id ? (
                        <span className="mono" style={{ fontSize: 12, color: "var(--blue)" }}>
                          {r.id}
                        </span>
                      ) : null}
                    </>
                  );
                  return r.to ? (
                    <button
                      type="button"
                      className="aitem link-row"
                      key={i}
                      onClick={() => navigate(r.to)}
                    >
                      {body}
                    </button>
                  ) : (
                    <div className="aitem" key={i}>
                      {body}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Crew */}
            <div className="card">
              <div className="card-head">
                <span
                  className="sec-ico"
                  style={{ background: "var(--surface-3)", color: "var(--ink-2)" }}
                >
                  <UserGroupIcon className="h-4 w-4" />
                </span>
                <h2>{t("work_order.redesign.crew")}</h2>
                <span className="sub">
                  {t("work_order.redesign.assigned_count", { count: crew.length })}
                </span>
                <span className="spacer" />
                {canEditWorkOrderUi ? (
                  <button
                    type="button"
                    className="wbtn sm ghost"
                    onClick={() => setEditDrawerOpen(true)}
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              <div className="alist">
                {crew.map((p, i) => {
                  const name = p.user?.displayTitle || p.user?.name || "—";
                  const palette = [
                    "#0E8C7B",
                    "#2563EB",
                    "#C2710C",
                    "#7A5AF0",
                    "#15924E",
                    "#D6443A",
                  ];
                  const color = palette[i % palette.length];
                  const init = name
                    .split(" ")
                    .map((x) => x[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase();
                  return (
                    <div className="aitem" key={p.id || i}>
                      <span
                        className="av"
                        style={{ background: color, width: 34, height: 34 }}
                      >
                        {init}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="ai-t">{name}</div>
                        <div className="ai-s">
                          {t(`work_order.participants.roles.${p.role}`, p.role)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {crew.length === 0 && (
                  <div className="empty">{t("work_order.redesign.no_crew")}</div>
                )}
              </div>
            </div>

            {showWorkOrderAdminMenu ? (
              <WorkOrderAdminMenu
                workOrder={work_order}
                hasAccess={showWorkOrderAdminMenu}
                onOpenApprove={() => setApproveDialogOpen(true)}
                onOpenBilling={() => setBillingReadyDialogOpen(true)}
                onOpenFinish={() => setWorkOrderFinishDialogOpen(true)}
                onOpenAdminSetStatus={() => setAdminSetStatusDialogOpen(true)}
                canApprove={canShowWorkOrderApproveInMenu}
                canMarkReadyForInvoicing={canMarkReadyForInvoicing}
                canOpenLinkedSalesOrder={canOpenLinkedSalesOrder}
                canEdit={canEditWorkOrderUi}
                canOpenFinish={canUseFinishDialog}
                canAdminSetStatus={canAdminSetStatus}
              />
            ) : null}

            <div style={{ display: "none" }}>
              {/* legacy buckets to keep status label mapping referenced */}
              {statusLabel}
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    budgetedHours,
    totalRegisteredHours,
    totalMaterialCosts,
    work_order,
    canAccessProjectPlan,
    project,
    canEditWorkOrderUi,
    canUseFinishDialog,
    timeRegistrationDialogSignal,
    showWorkOrderAdminMenu,
    canShowWorkOrderApproveInMenu,
    canMarkReadyForInvoicing,
    canOpenLinkedSalesOrder,
    canAdminSetStatus,
    permissions,
    navigate,
    dispatch,
    hoursController,
    t,
    qualityChecklistsModule,
    setEditDrawerOpen,
    scrollToStatusUpdates,
    setItemConsumptionDialogOpen,
    setTimeRegistrationDialogSignal,
    setWorkOrderFinishDialogOpen,
    setApproveDialogOpen,
    setBillingReadyDialogOpen,
    setAdminSetStatusDialogOpen,
  ]);

  // Access Denied Component
  const AccessDenied = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Tilgang nektet
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Du har ikke tilgang til denne arbeidsordren
          </p>
        </div>
        <div className="mt-8 space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-4">
              Kontakt administrator hvis du mener du skal ha tilgang til denne
              arbeidsordren.
            </p>
            <PMButton
              onClick={() => navigate("/dashboard/work_orders/")}
              className="w-full"
            >
              GÃ¥ tilbake til oversikt
            </PMButton>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {accessDenied ? (
        <AccessDenied />
      ) : (
        <DashboardHeader
          variant="v2"
          meta={editing ? [] : headerMeta}
          summaryItems={editing ? [] : headerSummaryItems}
          summaryFooter={editing ? null : headerSummaryFooter}
          actions={headerActions}
          customHeader={
            !editing && work_order?.id && !isLoading ? (
              <div className="wo-redesign">
                <div className="hero" style={{ padding: 0 }}>
                  <div className="hero-top">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          marginBottom: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {work_order?.number ? (
                          <span className="wo-id mono">#{work_order.number}</span>
                        ) : null}
                        {work_order?.is_urgent ? (
                          <span className="chip warn">
                            <FlagIcon className="h-3 w-3" />{" "}
                            {t("work_order.redesign.urgent")}
                          </span>
                        ) : null}
                        <span
                          className={
                            "status-pill " +
                            STATUS_BUCKET_CLASS[workOrderStatusBucket(work_order)]
                          }
                        >
                          <span className="dot" />{" "}
                          {t(
                            `work_order.redesign.status.${workOrderStatusBucket(
                              work_order
                            )}`
                          )}
                        </span>
                      </div>
                      <h1>{work_order?.title || "Arbeidsordre"}</h1>
                      <div className="chips" style={{ marginTop: 12 }}>
                        {work_order?.project?.displayTitle ? (
                          <span className="chip">
                            <MapPinIcon className="h-3.5 w-3.5" />{" "}
                            {work_order.project.displayTitle}
                          </span>
                        ) : null}
                        {work_order?.customer?.displayTitle ? (
                          <span className="chip">
                            <BuildingOffice2Icon className="h-3.5 w-3.5" />{" "}
                            {work_order.customer.displayTitle}
                          </span>
                        ) : null}
                        {work_order?.due_date ? (
                          <span
                            className={
                              "chip " +
                              (isWorkOrderOverdue(work_order) ? "over" : "warn")
                            }
                          >
                            <ClockIcon className="h-3.5 w-3.5" />{" "}
                            {t("work_order.redesign.due")}{" "}
                            {dateToString(work_order.due_date)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <ProgressRing
                      pct={
                        budgetedHours > 0
                          ? progressPercentage
                          : { done: 100, progress: 65, scheduled: 35, draft: 10 }[
                              workOrderStatusBucket(work_order)
                            ]
                      }
                    />
                  </div>
                  <div
                    className="chips"
                    style={{ marginTop: 4, justifyContent: "flex-end" }}
                  >
                    {canEditWorkOrderUi ||
                    permissions?.canExecute ||
                    permissions?.canAddNote ||
                    permissions?.canWorkNotes ? (
                      <button
                        type="button"
                        className="wbtn sm"
                        onClick={() => setEditDrawerOpen(true)}
                      >
                        <PencilIcon className="h-4 w-4" /> {t("edit")}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="wbtn sm"
                      onClick={() => setWorkOrderPrintDialogOpen(true)}
                    >
                      <PrinterIcon className="h-4 w-4" />{" "}
                      {t("work_order.redesign.print")}
                    </button>
                    {(canEditWorkOrderUi || permissions?.canFinishWorkOrder) &&
                    !work_order?.is_completed &&
                    ["draft", "ongoing", "rejected"].includes(
                      work_order?.status
                    ) ? (
                      <button
                        type="button"
                        className="wbtn sm primary"
                        onClick={() => setWorkOrderFinishDialogOpen(true)}
                      >
                        <CheckBadgeIcon className="h-4 w-4" />{" "}
                        {t("work_order.redesign.finish")}
                      </button>
                    ) : null}
                    {canDeleteWorkOrderInModule || isMananger ? (
                      <button
                        type="button"
                        className="wbtn sm danger"
                        onClick={() => setDeleteConfirmationOpen(true)}
                      >
                        <TrashIcon className="h-4 w-4" /> {t("delete")}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : undefined
          }
          title={
            editing
              ? work_order?.id
                ? work_order?.title || "Arbeidsordre"
                : "Ny arbeidsordre"
              : work_order?.title || "Arbeidsordre"
          }
          subtitle={
            editing
              ? work_order?.id
                ? "Rediger arbeidsordre"
                : "Ny arbeidsordre"
              : work_order?.displayTitle
                ? `Arbeidsordre / ${work_order.displayTitle}`
                : "Arbeidsordre"
          }
        >
          <>
            {isLoading ? (
              <ResourceLoading />
            ) : editing ? (
              <WorkOrderEditForm
                initialValues={initialValues}
                setInitialValues={setInitialValues}
                errors={errors}
                setErrors={setErrors}
                work_order={work_order}
                setWorkOrder={setWorkOrder}
                project={project}
                setProject={setProject}
                customer={customer}
                setCustomer={setCustomer}
                delivery_address={delivery_address}
                setDeliveryAddress={setDeliveryAddress}
                canMutateWorkOrderFields={canMutateWorkOrderFields}
                canEditWorkOrderUi={canEditWorkOrderUi}
                workOrderActivityListParams={workOrderActivityListParams}
                createOrUpdate={createOrUpdate}
                navigate={navigate}
                returnAfterWorkOrderCreate={returnAfterWorkOrderCreate}
                t={t}
                handleParticipantsChange={handleParticipantsChange}
                canAccessProjectPlan={canAccessProjectPlan}
                costEstimationOpen={costEstimationOpen}
                setCostEstimationOpen={setCostEstimationOpen}
                newProjectActivityTitle={newProjectActivityTitle}
                setNewProjectActivityTitle={setNewProjectActivityTitle}
                creatingProjectActivity={creatingProjectActivity}
                setCreatingProjectActivity={setCreatingProjectActivity}
                openRegistrationDialogSignal={timeRegistrationDialogSignal}
                permissions={permissions}
                onCancelEdit={() =>
                  navigate(
                    work_order?.id
                      ? `/dashboard/work_orders/${work_order.id}`
                      : "/dashboard/work_orders"
                  )
                }
              />
            ) : (
              <Content />
            )}
            {work_order?.id && !isLoading && (
              <nav
                className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
                style={{
                  paddingBottom:
                    "max(0.35rem, env(safe-area-inset-bottom, 0px))",
                }}
                aria-label={t("work_order.view.mobileNav.ariaLabel")}
              >
                <div className="flex items-stretch justify-around gap-0 px-0.5 pt-1">
                  {qualityChecklistsModule &&
                  (work_order?.uses_checklist ||
                    work_order?.checklist_id) ? (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/dashboard/work_orders/${work_order.id}/checklist`)
                      }
                      className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                    >
                      <ClipboardDocumentIcon
                        className="h-5 w-5 shrink-0"
                        aria-hidden
                      />
                      <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                        {t("work_orders.checklist.mobile_nav")}
                      </span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={!permissions.canItemConsumption}
                    onClick={() =>
                      navigate(
                        `/dashboard/work_orders/${work_order.id}/item-consumption`
                      )
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <CubeIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.items")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTimeRegistrationDialogSignal((n) => n + 1)
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <ClockIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.timeRegistration")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={scrollToStatusUpdates}
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("work_order.redesign.status_updates_title")}
                    </span>
                  </button>
                </div>
              </nav>
            )}
          </>
          {isMananger && (
            <PMDialog
              title="Godkjenn arbeidsordre"
              open={approveDialogOpen}
              onClose={() => setApproveDialogOpen(false)}
            >
              <Formik
                enableReinitialize={true} // Enable automatic reinitialization
                initialValues={{
                  id: work_order?.id,
                  is_approved: work_order?.is_approved,
                  is_rejected: work_order?.is_rejected,
                  approval_note: work_order?.approval_note,
                }} // Use the separate initialValues state
                validationSchema={Yup.object({
                  description: Yup.string(),
                })}
                onSubmit={(values, helpers) => {
                  axiosWithToast(
                    createOrUpdate(values),
                    (data) => {
                      // Extract the actual work order data from the response
                      const workOrderData = data?.data || data;
                      
                      if (workOrderData?.id) {
                        setWorkOrder(workOrderData); // Update work_order state
                        setInitialValues((prev) => ({
                          ...prev,
                          ...values,
                          id: workOrderData.id, // Update the ID with the actual ID from the response
                        }));
                        helpers.setStatus({ success: true });
                        helpers.setSubmitting(false);
                      } else {
                        console.error('No work order data received:', workOrderData);
                        helpers.setSubmitting(false);
                      }
                    },
                    () => {
                      helpers.setSubmitting(false); // Ensure submitting state is reset
                    },
                    () => {
                      setApproveDialogOpen(false);
                    },
                    "FravÃ¦r ble lagret",
                    (error) => {
                      setErrors(error); // Set errors for display
                      helpers.setErrors({
                        submit: "An error occurred while saving.",
                      }); // Update Formik errors
                      helpers.setSubmitting(false); // Reset submitting state
                    }
                  ).catch((err) => {
                    console.error("Submission error:", err);
                    helpers.setErrors({ submit: err.message });
                    helpers.setSubmitting(false);
                  });
                }}
              >
                {(formik) => (
                  <Form>
                    {errors &&
                      errors.length > 0 &&
                      errors.map((error, index) => (
                        <PMNotice
                          severity={"error"}
                          key={index}
                          title="Error!"
                          description={error}
                        />
                      ))}

                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
                      <div className="col-span-1 sm:col-span-1">
                        <PMSwitch
                          label="Godkjent"
                          name="is_approved"
                          {...formik}
                          onChange={(e) => {
                            formik.setFieldValue("is_rejected", false);
                            formik.handleChange(e);
                          }}
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-1">
                        <PMSwitch
                          label="Ikke godkjent"
                          name="is_rejected"
                          {...formik}
                          onChange={(e) => {
                            formik.setFieldValue("is_approved", false);
                            formik.handleChange(e);
                          }}
                        />
                      </div>

                      <div className="col-span-2 sm:col-span-2">
                        <PMField
                          label="Notat"
                          name="approval_note"
                          multiline={true}
                          rows={2}
                          {...formik}
                        />
                      </div>

                      <div className="col-span-2 sm:col-span-2 flex justify-between">
                        <PMButton
                          type="button"
                          onClick={() => setApproveDialogOpen(false)}
                          className="bg-slate-400 hover:bg-slate-200 hover-bg- max-w-1/2"
                        >
                          Avbryt
                        </PMButton>
                        <PMButton className="ml-10 max-w-1/2" type="submit">
                          Lagre
                        </PMButton>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </PMDialog>
          )}

          {!permissions.isClosed && (
            <>


              {/* Item Consumption Dialog */}
              {permissions.canItemConsumption && (
                <PMDialog
                  size="4xl"
                  title="Vareforbruk"
                  open={itemConsumptionDialogOpen}
                  onClose={() => {
                    setItemConsumptionDialogOpen(false);
                    // Clear any errors when dialog is closed
                    setErrors([]);
                  }}
                >
                  {itemConsumptionDialogOpen && (
                    <WorkOrderItemConsumptionList
                      workOrder={work_order}
                      disabled={!permissions.canItemConsumption}
                      onItemsUpdate={(items) => setWorkOrder((prev) => ({ ...prev, items }))}
                    />
                  )}
                </PMDialog>
              )}
            </>
          )}
        </DashboardHeader>
      )}

      {/* Right-side edit drawer (replaces the standalone edit page). */}
      {work_order?.id
        ? createPortal(
        <div className="wo-redesign">
          <div
            className={"wo-drawer-scrim" + (editDrawerOpen ? " show" : "")}
            onClick={closeEditDrawer}
          />
          <aside
            className={"wo-drawer" + (editDrawerOpen ? " show" : "")}
            role="dialog"
            aria-modal="true"
            aria-hidden={!editDrawerOpen}
          >
            <div className="wo-drawer-head">
              <span
                className="sec-ico"
                style={{ background: "var(--primary-tint)", color: "var(--primary)" }}
              >
                <PencilIcon className="h-[17px] w-[17px]" />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2>{t("work_orders.edit_details")}</h2>
                <div className="sub">
                  {work_order?.displayTitle ||
                    work_order?.title ||
                    (work_order?.number ? `#${work_order.number}` : "")}
                </div>
              </div>
              <button
                type="button"
                className="wbtn sm ghost icon"
                onClick={closeEditDrawer}
                aria-label={t("Close")}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="wo-drawer-body">
              {editDrawerOpen ? (
                <WorkOrderEditForm
                  initialValues={initialValues}
                  setInitialValues={setInitialValues}
                  errors={errors}
                  setErrors={setErrors}
                  work_order={work_order}
                  setWorkOrder={setWorkOrder}
                  project={project}
                  setProject={setProject}
                  customer={customer}
                  setCustomer={setCustomer}
                  delivery_address={delivery_address}
                  setDeliveryAddress={setDeliveryAddress}
                  canMutateWorkOrderFields={canMutateWorkOrderFields}
                  canEditWorkOrderUi={canEditWorkOrderUi}
                  workOrderActivityListParams={workOrderActivityListParams}
                  createOrUpdate={createOrUpdate}
                  navigate={navigate}
                  t={t}
                  handleParticipantsChange={handleParticipantsChange}
                  canAccessProjectPlan={canAccessProjectPlan}
                  costEstimationOpen={costEstimationOpen}
                  setCostEstimationOpen={setCostEstimationOpen}
                  newProjectActivityTitle={newProjectActivityTitle}
                  setNewProjectActivityTitle={setNewProjectActivityTitle}
                  creatingProjectActivity={creatingProjectActivity}
                  setCreatingProjectActivity={setCreatingProjectActivity}
                  openRegistrationDialogSignal={timeRegistrationDialogSignal}
                  permissions={permissions}
                  drawerMode
                  onSaved={closeEditDrawer}
                  onCancelEdit={closeEditDrawer}
                />
              ) : null}
            </div>
          </aside>
        </div>,
            document.body
          )
        : null}

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("work_orders.delete_confirm")}
        action="delete"
        module="work_order"
        yesCallback={handleDeleteWorkOrder}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />

      <WorkOrderPrintDialog
        open={workOrderPrintDialogOpen}
        onClose={() => setWorkOrderPrintDialogOpen(false)}
        workOrder={work_order}
        onRefresh={(data) => {
          if (data) {
            setWorkOrder(data);
          }
        }}
      />

      <WorkOrderFinishDialog
        open={workOrderFinishDialogOpen}
        onClose={() => setWorkOrderFinishDialogOpen(false)}
        selected={work_order}
        fields={initialValues}
        isLoading={isLoading}
        onRefresh={(data) => {
          if (!data) return;
          setWorkOrder((prev) =>
            prev?.id != null &&
            data.id != null &&
            String(prev.id) === String(data.id)
              ? { ...prev, ...data }
              : data
          );
        }}
      />

      <WorkOrderBillingReadyDialog
        open={billingReadyDialogOpen}
        onClose={() => setBillingReadyDialogOpen(false)}
        workOrder={work_order}
        hours={hoursState?.data || []}
        onSaved={(wo) => {
          if (wo) setWorkOrder(wo);
        }}
      />

      <WorkOrderAdminSetStatusDialog
        open={adminSetStatusDialogOpen}
        onClose={() => setAdminSetStatusDialogOpen(false)}
        workOrder={work_order}
        onSaved={(data) => {
          if (!data) return;
          setWorkOrder((prev) =>
            prev?.id != null &&
            data.id != null &&
            String(prev.id) === String(data.id)
              ? { ...prev, ...data }
              : prev
          );
        }}
      />
    </>
  );
};


